import { supabase } from "./client";
import { isSupabaseConfigured } from "./config";

const PUBLIC_DELIVERY_FUNCTION = "public-delivery-channel";

const ensureReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error("Delivery no esta configurado en este equipo.");
  }
};

const readFunctionError = async (error, fallbackMessage) => {
  const response = error?.context;

  if (response && typeof response.json === "function") {
    try {
      const payload = await response.json();
      if (payload?.error) return String(payload.error);
    } catch {
      // Conservamos el mensaje original si el cuerpo no se puede leer.
    }
  }

  return error?.message || fallbackMessage;
};

const invokeDelivery = async (body, fallbackMessage, { accessToken = "" } = {}) => {
  ensureReady();
  const { data, error } = await supabase.functions.invoke(PUBLIC_DELIVERY_FUNCTION, {
    body,
    ...(accessToken
      ? { headers: { Authorization: `Bearer ${accessToken}` } }
      : {}),
  });

  if (error) {
    throw new Error(await readFunctionError(error, fallbackMessage));
  }

  if (!data || data.ok === false) {
    throw new Error(data?.message || data?.error || fallbackMessage);
  }

  return data;
};

export const loadPublicDeliveryMenu = (deliveryToken) =>
  invokeDelivery(
    { action: "bootstrap", deliveryToken },
    "No pudimos abrir el menu de Delivery."
  );

export const identifyPublicDeliveryCustomer = ({ deliveryToken, phone }) =>
  invokeDelivery(
    { action: "identify", deliveryToken, phone },
    "No pudimos identificar tu numero de WhatsApp."
  );

export const bootstrapPublicDeliveryWeCustomer = ({
  deliveryToken,
  accessToken,
}) =>
  invokeDelivery(
    { action: "we_session", deliveryToken },
    "No pudimos vincular tu cuenta de We! pedidos con este restaurante.",
    { accessToken }
  );

export const loadPublicDeliverySession = ({ deliveryToken, sessionToken }) =>
  invokeDelivery(
    { action: "session", deliveryToken, sessionToken },
    "Tu sesion de Delivery ya no esta disponible."
  );

export const loadPublicDeliverySessionOrders = ({
  deliveryToken,
  sessionToken,
}) =>
  invokeDelivery(
    { action: "session_orders", deliveryToken, sessionToken },
    "No pudimos recuperar los pedidos de esta sesion."
  );

export const loadRememberedPublicDeliveryOrders = async () => {
  if (typeof window === "undefined") return [];
  const prefix = "mozzo:public-delivery-session:";
  const references = Object.keys(window.sessionStorage)
    .filter((key) => key.startsWith(prefix))
    .map((key) => ({
      deliveryToken: key.slice(prefix.length),
      sessionToken: window.sessionStorage.getItem(key) || "",
    }))
    .filter((reference) => reference.deliveryToken && reference.sessionToken);

  const responses = await Promise.allSettled(
    references.map(loadPublicDeliverySessionOrders)
  );
  return responses.flatMap((result) =>
    result.status === "fulfilled" && Array.isArray(result.value?.orders)
      ? result.value.orders
      : []
  );
};

export const registerPublicDeliveryCustomer = ({
  deliveryToken,
  sessionToken,
  profile,
  deliveryZoneId,
  addressPersistence = "permanent",
}) =>
  invokeDelivery(
    {
      action: "register",
      deliveryToken,
      sessionToken,
      ...profile,
      deliveryZoneId,
      addressPersistence,
    },
    "No pudimos guardar tus datos de Delivery."
  );

export const validatePublicDeliveryAddress = ({
  deliveryToken,
  sessionToken,
  profile,
}) =>
  invokeDelivery(
    {
      action: "validate_address",
      deliveryToken,
      sessionToken,
      ...profile,
    },
    "No pudimos validar la distancia de entrega."
  );

export const locatePublicDeliveryAddress = ({
  deliveryToken,
  sessionToken,
  latitude,
  longitude,
}) =>
  invokeDelivery(
    {
      action: "locate_address",
      deliveryToken,
      sessionToken,
      latitude,
      longitude,
    },
    "No pudimos identificar la direccion elegida."
  );

export const createPublicDeliveryOrder = ({
  deliveryToken,
  sessionToken,
  items,
  paymentMethod,
  expectedTotal,
  sourceChannel,
  customerNotes,
  requestId,
  returnUrl = "",
  accessToken = "",
}) =>
  invokeDelivery(
    {
      action: "create_order",
      deliveryToken,
      sessionToken,
      items,
      paymentMethod,
      expectedTotal,
      sourceChannel,
      customerNotes,
      requestId,
      returnUrl,
      linkWeCustomerAccount: Boolean(accessToken),
    },
    "No pudimos enviar el pedido de Delivery.",
    { accessToken }
  );
